import React, { CSSProperties, ReactNode } from "react";

import { ClassNameFormatter } from "@bem-react/classname";
import { createInitFrameAction } from "@multiweb/frames";
import { GridItemsHeight, GridLayoutItem, GridLayoutItemProps, MountHtmlElement } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { StoreManager } from "@multiweb/reactSetup";
import { bemClassNames, EMPTY_OBJECT, safeParseInt } from "@multiweb/reactUtils";
import { AppContext } from "App/AppContext";
import { UUID } from "Global/Base/IDs/UUID";
import { FormControlParams } from "UI/UI/UI/Controls/ControlInterfaces";
import { IForm } from "UI/UI/UI/XGControls/IForm";
import { ClassNamesModifiersType, IItemRenderer } from "../types";

const { dispatchAction } = bridge;

const classNames = bemClassNames("general-item-renderer");
export class GeneralItemRenderer implements IItemRenderer {
    public readonly element: HTMLElement;
    public readonly innerElement: HTMLElement;
    public readonly ownerForm: IForm;
    private readonly _gridItemsHeight: GridItemsHeight;
    private readonly _isPlacedDirectlyOnForm: boolean;
    public readonly gridItemProps: GridLayoutItemProps;

    public get innerElementHeight(): number | string | undefined {
        return safeParseInt(this.element.getAttribute("data-height"));
    }

    constructor(params: FormControlParams, gridItemsHeight: GridItemsHeight = GridItemsHeight.keepHeight) {
        this.element = params.element;
        this.innerElement = params.element.firstElementChild as HTMLElement;
        this.ownerForm = params.ownerForm;
        this._gridItemsHeight = gridItemsHeight;
        // prvni parent je GridLayout element z definice. Druhý je element, který obaluje Grid.
        this._isPlacedDirectlyOnForm = this.element?.parentElement?.parentElement?.classList.contains("FormInternalPanel");
        this.gridItemProps = {
            colSpan: safeParseInt(this.element.dataset.colspan),
            colSpanS: safeParseInt(this.element.dataset.colspans),
            colSpanM: safeParseInt(this.element.dataset.colspanm),
            colSpanL: safeParseInt(this.element.dataset.colspanl),
            colSpanXl: safeParseInt(this.element.dataset.colspanxl),
            colSpanXxl: safeParseInt(this.element.dataset.colspanxxl),
            rowSpan: safeParseInt(this.element.dataset.rowspan),
            rowSpanS: safeParseInt(this.element.dataset.rowspans),
            rowSpanM: safeParseInt(this.element.dataset.rowspanm),
            rowSpanL: safeParseInt(this.element.dataset.rowspanl),
            rowSpanXl: safeParseInt(this.element.dataset.rowspanxl),
            rowSpanXxl: safeParseInt(this.element.dataset.rowspanxxl),
        };
        const isDev = !!AppContext.instance.configuration.developmentMode;
        StoreManager.initializeStore(isDev);
        dispatchAction(createInitFrameAction(this.innerElement.id, true, true));
    }

    public initialize(): void {
        // Prázdná metoda
    }

    public dispose(): void {
        // Možný úklid rendereru. V tomto rendereru není potřeba (nevyužíváme listenery, ...), ale kvůli interface metoda musí existovat.
    }

    public render(): ReactNode {
        return (
            <GridLayoutItem key={this.reactKey} {...this.gridItemProps}>
                {this.getMountedInnerElement()}
            </GridLayoutItem>
        );
    }

    public get classNameModifiers(): ClassNamesModifiersType {
        return {
            "is-placed-directly-on-form": this._isPlacedDirectlyOnForm,
            "item-heights": this._gridItemsHeight,
        };
    }

    public get classNames(): ClassNameFormatter {
        return classNames;
    }

    public get styles(): CSSProperties {
        return EMPTY_OBJECT;
    }

    public get reactKey(): string {
        return this.innerElement.id || UUID.generate().slice(0, 5);
    }

    public getMountedInnerElement(): ReactNode {
        return (
            <div className={this.classNames(this.classNameModifiers)} style={this.styles}>
                <MountHtmlElement
                    htmlElement={this.innerElement}
                    height={this.innerElementHeight}
                    flexGrow={this._gridItemsHeight === GridItemsHeight.takeHighest ? 1 : undefined}
                />
            </div>
        );
    }
}
